import type { MouseEvent } from "react";
import { useState } from "react";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { ProductFormProductLine } from "src/pages/Product/ProductForm/ProductForm.types";

// Utility function to get parent IDs for initial expansion
const getAllParentIds = (items: ItemProps[], id: string): string[] => {
    const getParentNode = (items: ItemProps[], id: string): ItemProps | undefined => {
        for (const item of items) {
            if (item.children) {
                if (item.children.some((child) => child.id === id)) {
                    return item;
                } else {
                    const parentNode = getParentNode(item.children, id);
                    if (parentNode) return parentNode;
                }
            }
        }
        return undefined;
    };

    const parentIds: string[] = [];
    let parent = getParentNode(items, id);
    while (parent) {
        parentIds.push(parent.id);
        parent = getParentNode(items, parent.id);
    }
    return parentIds;
};

export type ItemProps = {
    id: string;
    label: string;
    children?: ItemProps[];
};

interface CustomTreeItemProps {
    item: ItemProps;
    isExpanded: boolean;
    onToggleExpanded: (itemId: string) => void;
    onSelectItem: (itemId: string) => void;
    selectedItem: ProductFormProductLine | null;
    level: number;
}

const CustomTreeItem = ({
    item,
    isExpanded,
    onToggleExpanded,
    onSelectItem,
    selectedItem,
    level,
}: CustomTreeItemProps) => {
    const theme = useTheme();
    const hasChildren = item.children && item.children.length > 0;
    const isSelected = selectedItem?.["@id"] === item.id;

    const handleLabelClick = () => {
        // Label is always for selection
        onSelectItem(item.id);
    };

    const handleExpandClick = (event: MouseEvent) => {
        event.stopPropagation();
        onToggleExpanded(item.id);
    };

    const handleSelectClick = (event: MouseEvent) => {
        event.stopPropagation();
        onSelectItem(item.id);
    };

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                sx={{
                    padding: "4px 8px",
                    paddingLeft: `${level * 20 + 8}px`,
                    minHeight: "36px",
                    backgroundColor: isSelected ? theme.palette.primary[100] : "transparent",
                    "&:hover": {
                        backgroundColor: isSelected ? theme.palette.primary[200] : theme.custom.grey[200],
                    },
                }}
            >
                {/* Expand/Collapse Icon */}
                {hasChildren ?
                    <IconButton
                        size="small"
                        onClick={handleExpandClick}
                        sx={{
                            width: "24px",
                            height: "24px",
                            marginRight: "4px",
                        }}
                    >
                        {isExpanded ?
                            <ExpandMore fontSize="small" />
                        :   <ChevronRight fontSize="small" />}
                    </IconButton>
                :   <Box sx={{ width: "24px", marginRight: "4px" }} />}

                {/* Label - clickable for selection */}
                <Typography
                    variant="body"
                    onClick={handleLabelClick}
                    sx={{
                        flex: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        fontWeight: isSelected ? "bold" : "normal",
                        color: isSelected ? theme.palette.primary[700] : "inherit",
                        "&:hover": {
                            color: theme.palette.primary[600],
                        },
                    }}
                >
                    {item.label}
                </Typography>

                {/* Selection Indicator */}
                {isSelected && (
                    <CheckCircle
                        fontSize="small"
                        sx={{
                            color: theme.palette.primary[600],
                            marginLeft: "4px",
                        }}
                    />
                )}
            </Stack>

            {/* Render children */}
            {hasChildren && isExpanded && item.children && (
                <Box>
                    {item.children.map((child) => (
                        <CustomTreeItem
                            key={child.id}
                            item={child}
                            isExpanded={isExpanded}
                            onToggleExpanded={onToggleExpanded}
                            onSelectItem={onSelectItem}
                            selectedItem={selectedItem}
                            level={level + 1}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

type ProductLineFormTreeViewProps = {
    items: ItemProps[];
    onSelectedItem: (itemId: string | null) => void;
    selectedItem?: ProductFormProductLine | null;
};

export const ProductLineFormTreeView = ({
    items,
    onSelectedItem,
    selectedItem = null,
}: ProductLineFormTreeViewProps) => {
    const [expandedItems, setExpandedItems] = useState<string[]>(getAllParentIds(items, selectedItem?.["@id"] || ""));

    const handleToggleExpanded = (itemId: string) => {
        setExpandedItems((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
    };

    const handleSelectItem = (itemId: string) => {
        onSelectedItem(itemId);
    };

    const isExpanded = (itemId: string) => expandedItems.includes(itemId);

    return (
        <Box sx={{ width: "100%" }}>
            {items.map((item) => (
                <CustomTreeItem
                    key={item.id}
                    item={item}
                    isExpanded={isExpanded(item.id)}
                    onToggleExpanded={handleToggleExpanded}
                    onSelectItem={handleSelectItem}
                    selectedItem={selectedItem}
                    level={0}
                />
            ))}
        </Box>
    );
};
